import { expectOk, unwrap, type Schemas } from '@swarm/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, offline } from '../../lib/api';
import { meKey } from '../../lib/queryClient';
import { forgetLocalPushDevice } from '../pwa/push';

export type Me = Schemas['MeDto'];
export type RoleRef = Schemas['RoleRefDto'];
export type Role = Schemas['RoleDto'];
export type Permission = Schemas['Permission'];
export type PermissionGroup = Schemas['PermissionGroupDto'];
export type SaveRoleRequest = Schemas['SaveRoleRequest'];
export type Business = Schemas['BusinessDto'];
export type Member = Schemas['MemberDto'];
export type Invitation = Schemas['InvitationDto'];
export type InvitationInfo = Schemas['InvitationInfoDto'];
export type SessionInfo = Schemas['SessionDto'];
export type UpdateBusinessRequest = Schemas['UpdateBusinessRequest'];

const keys = {
  business: ['business'] as const,
  members: ['business', 'members'] as const,
  invitations: ['business', 'invitations'] as const,
  roles: ['business', 'roles'] as const,
  permissions: ['permissions'] as const,
  sessions: ['me', 'sessions'] as const,
  connections: ['me', 'connections'] as const,
  /** Public instance information incl. the business name (see `useMeta`). */
  meta: ['system', 'meta'] as const,
};

// --- Session and profile ----------------------------------------------------

export function useMe() {
  return useQuery({
    queryKey: meKey,
    queryFn: async () => unwrap(await api.GET('/me')),
    retry: false,
  });
}

function useSignedIn() {
  const client = useQueryClient();
  return (me: Me) => {
    client.setQueryData(meKey, me);
  };
}

export function useLogin() {
  const signedIn = useSignedIn();
  return useMutation({
    mutationFn: async (body: { email: string; password: string }) =>
      unwrap(await api.POST('/auth/login', { body })),
    onSuccess: signedIn,
  });
}

export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: async (email: string) =>
      expectOk(await api.POST('/auth/password-reset/request', { body: { email } })),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: async (body: { token: string; password: string }) =>
      expectOk(await api.POST('/auth/password-reset/confirm', { body })),
  });
}

export function useLogout() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      // Nothing private stays on the device: queue and cached answers go (ADR 0027).
      // First, so the queue cannot go out with the session while signing out (#190).
      await offline.clearPrivateData();
      await forgetLocalPushDevice();
      return expectOk(await api.POST('/auth/logout'));
    },
    onSettled: async () => {
      client.clear();
      // Answers cached while the sign-out was running.
      await offline.clearPrivateData();
    },
  });
}

export function useUpdateProfile() {
  const signedIn = useSignedIn();
  return useMutation({
    mutationFn: async (body: { first_name: string; last_name: string; locale: string }) =>
      unwrap(await api.PATCH('/me', { body })),
    onSuccess: signedIn,
  });
}

export function useChangePassword() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: { current_password: string; new_password: string }) =>
      expectOk(await api.POST('/me/password', { body })),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.sessions }),
  });
}

export function useSessions() {
  return useQuery({
    queryKey: keys.sessions,
    queryFn: async () => unwrap(await api.GET('/me/sessions')),
  });
}

export function useRevokeSession() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId: string) =>
      expectOk(
        await api.DELETE('/me/sessions/{session_id}', {
          params: { path: { session_id: sessionId } },
        }),
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.sessions }),
  });
}

// --- Connected apps (MCP clients, MCP-005) --------------------------------------

export type Connection = Schemas['ConnectionDto'];
export type AuthorizationParams = { [K in keyof Schemas['AuthorizationParams']]?: string };
export type AuthorizationPrompt = Schemas['AuthorizationPromptDto'];
export type Scope = Schemas['ScopeDto'];

export function useConnections() {
  return useQuery({
    queryKey: keys.connections,
    queryFn: async () => unwrap(await api.GET('/me/connections')),
  });
}

export function useRevokeConnection() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (connectionId: string) =>
      expectOk(
        await api.DELETE('/me/connections/{connection_id}', {
          params: { path: { connection_id: connectionId } },
        }),
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.connections }),
  });
}

/** Checks the authorization request of an MCP client for the consent page. */
export function useAuthorizationPrompt(params: AuthorizationParams) {
  return useQuery({
    queryKey: ['oauth', 'authorization', params],
    queryFn: async () =>
      unwrap(await api.GET('/oauth/authorization', { params: { query: params } })),
    retry: false,
  });
}

/** The member's decision; resolves to the URL to send the browser to. */
export function useDecideAuthorization() {
  return useMutation({
    mutationFn: async (body: Schemas['AuthorizationDecisionRequest']) =>
      unwrap(await api.POST('/oauth/authorization', { body })).redirect_to,
  });
}

// --- Business ---------------------------------------------------------------

const ifMatch = (version: number) => ({ 'If-Match': `"${version}"` });

export function useBusiness() {
  return useQuery({
    queryKey: keys.business,
    queryFn: async () => unwrap(await api.GET('/business')),
  });
}

export function useUpdateBusiness() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ body, version }: { body: UpdateBusinessRequest; version: number }) =>
      unwrap(await api.PUT('/business', { body, headers: ifMatch(version) })),
    onSuccess: (business) => {
      client.setQueryData(keys.business, business);
      // The name is shown in the app frame and on the sign-in page.
      void client.invalidateQueries({ queryKey: keys.meta });
    },
  });
}

export function useUpdateBusinessSettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      body,
      version,
    }: {
      body: { timezone: string; locale: string };
      version: number;
    }) => unwrap(await api.PUT('/business/settings', { body, headers: ifMatch(version) })),
    onSuccess: (business) => client.setQueryData(keys.business, business),
  });
}

export function useMembers(enabled = true) {
  return useQuery({
    queryKey: keys.members,
    queryFn: async () => unwrap(await api.GET('/members')),
    enabled,
  });
}

/** One member with the last activity (#26). */
export function useMember(userId: string) {
  return useQuery({
    queryKey: [...keys.members, userId],
    queryFn: async () =>
      unwrap(await api.GET('/members/{user_id}', { params: { path: { user_id: userId } } })),
    enabled: userId !== '',
  });
}

export function useChangeRole() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, roleId }: { userId: string; roleId: string }) =>
      expectOk(
        await api.PATCH('/members/{user_id}', {
          params: { path: { user_id: userId } },
          body: { role_id: roleId },
        }),
      ),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.members });
      void client.invalidateQueries({ queryKey: keys.roles });
      void client.invalidateQueries({ queryKey: meKey });
    },
  });
}

/** Removes a member; their account is deleted with it. */
export function useRemoveMember() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) =>
      expectOk(
        await api.DELETE('/members/{user_id}', {
          params: { path: { user_id: userId } },
        }),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.members });
      void client.invalidateQueries({ queryKey: meKey });
    },
  });
}

export function useInvitations(enabled: boolean) {
  return useQuery({
    queryKey: keys.invitations,
    queryFn: async () => unwrap(await api.GET('/invitations')),
    enabled,
  });
}

export function useInvite() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: { email: string; role_id: string }) =>
      unwrap(await api.POST('/invitations', { body })),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.invitations }),
  });
}

export function useRevokeInvitation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (invitationId: string) =>
      expectOk(
        await api.DELETE('/invitations/{invitation_id}', {
          params: { path: { invitation_id: invitationId } },
        }),
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.invitations }),
  });
}

// --- Roles (USR-009) ---------------------------------------------------------

/** The permission catalog; static per server version. */
export function usePermissionCatalog() {
  return useQuery({
    queryKey: keys.permissions,
    queryFn: async () => unwrap(await api.GET('/permissions')),
    staleTime: Infinity,
  });
}

export function useRoles(enabled = true) {
  return useQuery({
    queryKey: keys.roles,
    queryFn: async () => unwrap(await api.GET('/roles')),
    enabled,
  });
}

function useRolesChanged() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: keys.roles });
    void client.invalidateQueries({ queryKey: keys.members });
    void client.invalidateQueries({ queryKey: keys.invitations });
  };
}

export function useCreateRole() {
  const changed = useRolesChanged();
  return useMutation({
    mutationFn: async (body: SaveRoleRequest) => unwrap(await api.POST('/roles', { body })),
    onSuccess: changed,
  });
}

export function useUpdateRole() {
  const changed = useRolesChanged();
  return useMutation({
    mutationFn: async ({ roleId, body }: { roleId: string; body: SaveRoleRequest }) =>
      unwrap(
        await api.PUT('/roles/{role_id}', {
          params: { path: { role_id: roleId } },
          body,
        }),
      ),
    onSuccess: changed,
  });
}

export function useDeleteRole() {
  const changed = useRolesChanged();
  return useMutation({
    mutationFn: async (roleId: string) =>
      expectOk(
        await api.DELETE('/roles/{role_id}', {
          params: { path: { role_id: roleId } },
        }),
      ),
    onSuccess: changed,
  });
}

// --- Invitations (landing page) ----------------------------------------------

export function useInvitationInfo(token: string) {
  return useQuery({
    queryKey: ['invitation', token],
    queryFn: async () => unwrap(await api.POST('/invitations/lookup', { body: { token } })),
    enabled: token.length > 0,
    retry: false,
  });
}

export function useRegisterWithInvitation() {
  const signedIn = useSignedIn();
  return useMutation({
    mutationFn: async (body: {
      token: string;
      first_name: string;
      last_name: string;
      password: string;
    }) => unwrap(await api.POST('/invitations/register', { body })),
    onSuccess: signedIn,
  });
}
